import { useEffect, useState } from 'react';
import {
  CalculatorLineChart,
  Divider,
  Flex,
  Loader,
  SliderContainer,
  Text,
  TwoLabelToggle,
} from '@components';
import {
  Chart,
  chartData,
  Frequency,
  PointOfContribution,
} from './ProductCalculator.const';
import {
  StyledChartContainer,
  StyledContainer,
  StyledSliderContainer,
} from './ProductCalculator.styles';
import { Box, useTheme } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { Calculator } from '@features/api/response/tabContent/Calculator';
import GlobalIntlSingleton from '@setup/intl/GlobalIntlSingleton';
import { useTabData } from '@hooks/useTabData';
import { Config } from '@config/config';
import { TabType } from '@features/api/response/tabContent/TabType';

const ProductCalculator: React.FC = () => {
  const theme = useTheme();
  const intl = useIntl();
  const {
    data: calculator,
    isFetching,
    isLoading,
  } = useTabData<Calculator>(TabType.CALCULATOR);

  const [amountInvested, setAmountInvested] =
    useState<number | number[]>(125000);
  const [additionalContribution, setAdditionalContribution] =
    useState<number | number[]>(0);
  const [frequency, setFrequency] = useState<string>(Frequency.Monthly);
  const [contribution, setContribution] = useState<string>(
    PointOfContribution.Beginning
  );
  const [yearsToGrow, setYearsToGrow] = useState<number | number[]>(10);
  const [estimatedRateofReturn, setEstimatedRateofReturn] =
    useState<number | number[]>(10);
  const [chart, setChart] = useState(chartData);
  const [isPageLoaded, setIsPageLoaded] = useState<boolean>(false);

  const getMean = (start: string, end: string): number => {
    return Math.round((Number(start) + Number(end)) / 2);
  };

  useEffect(() => {
    if (calculator) {
      setYearsToGrow(
        getMean(
          calculator.yearsToGrow.toValue,
          calculator.yearsToGrow.fromValue
        )
      );
      setEstimatedRateofReturn(
        getMean(
          calculator.estimatedRateOfReturn.toValue,
          calculator.estimatedRateOfReturn.fromValue
        )
      );
      setIsPageLoaded(true);
    }
  }, [calculator]);

  useEffect(() => {
    businessLogicForCalculator();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    amountInvested,
    additionalContribution,
    yearsToGrow,
    estimatedRateofReturn,
    contribution,
    frequency,
  ]);

  const getReturnRate = (updateAmountWithRate: number): number => {
    const additionalContro = Number(additionalContribution);
    const interestRate = Number(estimatedRateofReturn);
    if (frequency === Frequency.Monthly) {
      let returnRate = 0;
      Array.from({ length: 12 }, (_, i) => i + 1).forEach((x) => {
        if (contribution === PointOfContribution.End) {
          if (x === 12) {
            returnRate += (updateAmountWithRate * (interestRate / 12)) / 100;
          } else {
            returnRate +=
              ((updateAmountWithRate + additionalContro * x) *
                (interestRate / 12)) /
              100;
          }
        } else {
          returnRate +=
            ((updateAmountWithRate + additionalContro * x) *
              (interestRate / 12)) /
            100;
        }
      });
      return returnRate;
    } else {
      const returnRate =
        contribution === PointOfContribution.End
          ? (updateAmountWithRate * interestRate) / 100
          : ((updateAmountWithRate + additionalContro) * interestRate) / 100;

      return returnRate;
    }
  };

  const getAdditionalContribution = (year: number): number => {
    const additionalContro =
      frequency === Frequency.Monthly
        ? Number(additionalContribution) * 12
        : Number(additionalContribution);
    if (frequency === Frequency.Monthly) {
      if (year === 0 && contribution === PointOfContribution.Beginning) {
        return Number(additionalContribution);
      }
      return additionalContro * year;
    } else {
      if (year === 0 && contribution === PointOfContribution.Beginning) {
        return Number(additionalContribution);
      }
      return additionalContro * year;
    }
  };

  const businessLogicForCalculator = (): void => {
    const investmentAmount = Number(amountInvested);
    const additionalContro =
      frequency === Frequency.Monthly
        ? Number(additionalContribution) * 12
        : Number(additionalContribution);
    const generatedArray = Array.from(
      { length: Number(yearsToGrow) },
      (_, i) => i + 1
    );
    generatedArray.unshift(0);

    const tempChartData = chart.map((item) => {
      if (item.id === Chart.INITIALINVESTMENT) {
        const initialTemp = generatedArray.map((_, i) => ({
          x: i,
          y: investmentAmount,
          initialInvestment: investmentAmount,
          additionalContribution: 0,
          valueCreation: 0,
        }));
        return {
          ...item,
          data: initialTemp,
        };
      } else if (item.id === Chart.ADDITIONALCONTRIBUTION) {
        const additionalTemp = generatedArray.map((_, i) => ({
          x: i,
          y: investmentAmount + getAdditionalContribution(i),
          initialInvestment: investmentAmount,
          additionalContribution: getAdditionalContribution(i),
          valueCreation: 0,
        }));
        return {
          ...item,
          data: additionalTemp,
        };
      } else {
        let updateAmountWithRate = investmentAmount;
        const valueCreationTemp = generatedArray.map((_, i) => {
          if (i > 0) {
            const returnRate = getReturnRate(updateAmountWithRate);
            updateAmountWithRate += additionalContro + returnRate;
          }
          return {
            x: i,
            y: Math.round(updateAmountWithRate),
            initialInvestment: investmentAmount,
            additionalContribution: getAdditionalContribution(i),
            valueCreation: Math.round(
              updateAmountWithRate - additionalContro * i - investmentAmount
            ),
          };
        });

        return {
          ...item,
          data: valueCreationTemp,
        };
      }
    });
    setChart([...tempChartData]);
  };

  if (!isPageLoaded) {
    return <Loader isLoading={isFetching || isLoading} />;
  }
  return (
    <StyledContainer>
      <StyledSliderContainer>
        <Box sx={{ mb: 8 }}>
          <SliderContainer
            label={<FormattedMessage id='product.calculator.amountInvested' />}
            startValueAdornment={Config.currency.symbol}
            step={10000}
            max={Number(calculator?.amountToBeInvested?.toValue)}
            min={Number(calculator?.amountToBeInvested?.fromValue)}
            value={amountInvested}
            hasInput
            onChange={(_: Event | undefined, value: number | number[]) => {
              setAmountInvested(value);
            }}
          />
        </Box>
        <Box sx={{ mb: 8 }}>
          <SliderContainer
            label={
              <FormattedMessage id='product.calculator.additionalContribution' />
            }
            startValueAdornment={Config.currency.symbol}
            step={1000}
            max={Number(calculator?.additionalContribution?.toValue)}
            min={Number(calculator?.additionalContribution?.fromValue)}
            value={additionalContribution}
            hasInput
            onChange={(_: Event | undefined, value: number | number[]) => {
              setAdditionalContribution(value);
            }}
          >
            <Flex flex={1} flexDirection='column'>
              <Text
                variant='bodySmall'
                fontWeight='fontWeightRegular'
                color={theme.palette.grey.A100}
              >
                <FormattedMessage id='product.calculator.choosefrequency' />
              </Text>
              <Flex gap={12} mt={4}>
                <TwoLabelToggle
                  firstLabel={
                    <FormattedMessage id='product.calculator.monthly' />
                  }
                  secondLabel={
                    <FormattedMessage id='product.calculator.yearly' />
                  }
                  checked={frequency === Frequency.Yearly}
                  onChange={(_, checked) =>
                    setFrequency(checked ? Frequency.Yearly : Frequency.Monthly)
                  }
                />
              </Flex>
              <Text
                variant='bodySmall'
                fontWeight='fontWeightRegular'
                color={theme.palette.grey.A100}
                sx={{ mt: 10 }}
              >
                <FormattedMessage id='product.calculator.pointOfContribution' />
              </Text>
              <Flex gap={12} mt={4}>
                <TwoLabelToggle
                  firstLabel={
                    <FormattedMessage id='product.calculator.beginning' />
                  }
                  secondLabel={<FormattedMessage id='product.calculator.end' />}
                  checked={contribution === PointOfContribution.End}
                  onChange={(_, checked) =>
                    setContribution(
                      checked
                        ? PointOfContribution.End
                        : PointOfContribution.Beginning
                    )
                  }
                />
              </Flex>
            </Flex>
          </SliderContainer>
        </Box>
        <Box sx={{ mb: 8 }}>
          <SliderContainer
            label={<FormattedMessage id='product.calculator.yearsToGrow' />}
            endValueAdornment={intl.formatMessage({
              id: 'product.calculator.years',
            })}
            step={1}
            max={Number(calculator?.yearsToGrow?.toValue)}
            min={Number(calculator?.yearsToGrow?.fromValue)}
            {...calculator?.yearsToGrow}
            value={yearsToGrow}
            onChange={(_: Event | undefined, value: number | number[]) => {
              setYearsToGrow(value);
            }}
          />
        </Box>
        <Box sx={{ mb: 8 }}>
          <SliderContainer
            label={
              <FormattedMessage id='product.calculator.estimatedRateofReturn' />
            }
            endValueAdornment='%'
            step={1}
            max={Number(calculator?.estimatedRateOfReturn?.toValue)}
            min={Number(calculator?.estimatedRateOfReturn?.fromValue)}
            value={estimatedRateofReturn}
            onChange={(_: Event | undefined, value: number | number[]) => {
              setEstimatedRateofReturn(value);
            }}
          >
            <Text
              variant='bodySmall'
              fontWeight='fontWeightRegular'
              color={theme.palette.grey.A100}
            >
              <FormattedMessage
                id='product.calculator.targetReturn'
                values={{
                  low: calculator?.targetReturn?.fromValue ?? '',
                  high: calculator?.targetReturn?.toValue ?? '',
                }}
              />
            </Text>
          </SliderContainer>
        </Box>
      </StyledSliderContainer>
      <Divider flexItem orientation='vertical' sx={{ order: 2 }} />
      <StyledChartContainer>
        <CalculatorLineChart
          data={chart}
          title={GlobalIntlSingleton.intl.formatMessage({
            id: 'product.calculator.investmentGrowthOverTime',
          })}
        />
      </StyledChartContainer>
    </StyledContainer>
  );
};

export default ProductCalculator;
